import { templateFrameStatus } from '@designdeck/shared';
import type {
  AdminMockupTemplate as ProductMockupTemplate,
  AdminProductPosition,
} from '@/api/admin';

/**
 * Pure queue logic for the frame-review walkthrough: the order in which
 * (position, template) pairs are stepped through. Kept out of the page
 * component so it can be unit-tested without rendering.
 */

/** Queue order inside a product: front, then back, then the rest. */
export function positionRank(slug: string): number {
  if (slug === 'front') return 0;
  if (slug === 'back') return 1;
  return 2;
}

export interface QueueEntry {
  position: AdminProductPosition;
  template: ProductMockupTemplate;
}

/**
 * Whether a template is still to calibrate: its stored status is
 * UNCALIBRATED or NEEDS_REVIEW, or its position lacks the millimetres it
 * would be calibrated against. The same rule the API counts as
 * `templatesToCalibrate`.
 */
export function isTemplateOpen(
  position: AdminProductPosition,
  template: ProductMockupTemplate
): boolean {
  return templateFrameStatus(position.printArea, template) !== 'calibrated';
}

/**
 * All (position, template) pairs of a product, ordered the way the walkthrough
 * steps through them: the default templates first (they decide whether the
 * product is selectable, R13), then the others; within each group front,
 * then back, then other positions. `include` narrows the pairs, e.g. to the
 * open ones (`isTemplateOpen`).
 */
export function buildEntries(
  positions: AdminProductPosition[] | undefined,
  templates: ProductMockupTemplate[] | undefined,
  include: (entry: QueueEntry) => boolean = () => true
): QueueEntry[] {
  if (!positions || !templates) return [];
  const ordered = [...positions].sort(
    (a, b) =>
      positionRank(a.slug) - positionRank(b.slug) || a.sortOrder - b.sortOrder
  );
  const entries: QueueEntry[] = [];
  for (const position of ordered) {
    for (const template of templates) {
      if (template.positionKey !== position.slug) continue;
      const entry = { position, template };
      if (include(entry)) entries.push(entry);
    }
  }
  // A stable sort keeps the position order inside each group.
  return entries.sort(
    (a, b) =>
      Number(b.template.isDefault === true) -
      Number(a.template.isDefault === true)
  );
}

/** The open (position, template) pairs of a product, defaults first. */
export function buildOpenEntries(
  positions: AdminProductPosition[] | undefined,
  templates: ProductMockupTemplate[] | undefined
): QueueEntry[] {
  return buildEntries(positions, templates, (entry) =>
    isTemplateOpen(entry.position, entry.template)
  );
}

/**
 * The entries in a fixed order: by their template's place in `order` (the
 * template ids as the walkthrough first showed the product), entries not
 * named there last in their given order. A refetch of the product (each
 * save reloads it) may return the templates in another order; the steps
 * must not move under the admin, or "Speichern & weiter" lands on the
 * template it just saved.
 */
export function inFixedOrder(
  entries: readonly QueueEntry[],
  order: readonly string[]
): QueueEntry[] {
  const rank = new Map(order.map((id, index) => [id, index]));
  const rankOf = (entry: QueueEntry) =>
    rank.get(entry.template.id) ?? Number.MAX_SAFE_INTEGER;
  return [...entries].sort((a, b) => rankOf(a) - rankOf(b));
}
